import React, { useCallback, useEffect, useState, createElement } from 'react';
import jsPDF from 'jspdf';
import { FileTextIcon, DownloadIcon, CalendarIcon, MapPinIcon, DollarSignIcon, ShoppingCartIcon, TrendingUpIcon, CheckCircleIcon, PrinterIcon, BarChart3Icon } from 'lucide-react';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { PageHeader } from '../components/ui/PageHeader';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Badge } from '../components/ui/Badge';
import { StatsCard } from '../components/ui/StatsCard';
import { Pagination } from '../components/ui/Pagination';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { useSortableData } from '../hooks/useSortableData';
import { SortableTableHeader } from '../components/ui/SortableTableHeader';
import { Visit } from '../types';
import { supabaseAdmin } from '../lib/supabase';
import { useRealtimeRefresh } from '../hooks/useRealtimeRefresh';
type ReportType = 'diario' | 'semanal' | 'cobros' | 'ventas' | 'visitas' | 'productividad';
interface SaleItem {
  id: string;
  clienteNombre: string;
  vendedorId: string;
  vendedorNombre: string;
  fecha: Date;
  zona: string;
  montoTotal: number;
  subTotal: number;
  itbis: number;
  referencia: string;
  comentarios: string;
  source: 'visita' | 'factura';
}
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/x7GeSJnrf7zHwSFqA8Zq1u/Captura_de_pantalla_2026-02-19_124313-removebg-preview.png';
async function getLogoBase64(): Promise<string | null> {
  try {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject();
      img.src = LOGO_URL;
    });
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth || 100;
    canvas.height = img.naturalHeight || 100;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(img, 0, 0);
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}
// Draw a table manually using jsPDF primitives
function drawTable(doc: jsPDF, headers: string[], rows: string[][], startY: number, ml: number, mr: number, colWidths: number[], rowHeight: number = 7, subNotes: string[] = []): number {
  const ph = doc.internal.pageSize.getHeight();
  const tableW = colWidths.reduce((a, b) => a + b, 0);
  let y = startY;
  const drawHeaderRow = (yPos: number) => {
    doc.setFillColor(13, 27, 70);
    doc.rect(ml, yPos, tableW, rowHeight + 1, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    let x = ml;
    headers.forEach((h, i) => {
      doc.text(h, x + colWidths[i] / 2, yPos + rowHeight - 1.5, {
        align: 'center'
      });
      x += colWidths[i];
    });
    return yPos + rowHeight + 1;
  };
  y = drawHeaderRow(y);
  rows.forEach((row, rowIdx) => {
    const note = subNotes[rowIdx] || '';
    const totalH = rowHeight + (note ? 5.5 : 0);
    // Page break check
    if (y + totalH + 2 > ph - 14) {
      doc.addPage();
      y = 14;
      y = drawHeaderRow(y);
    }
    // Row background
    doc.setFillColor(rowIdx % 2 === 0 ? 255 : 248, rowIdx % 2 === 0 ? 255 : 250, rowIdx % 2 === 0 ? 255 : 255);
    doc.rect(ml, y, tableW, rowHeight, 'F');
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.2);
    doc.rect(ml, y, tableW, rowHeight, 'S');
    // Cell text
    doc.setFontSize(7.5);
    let x = ml;
    row.forEach((cell, colIdx) => {
      const cellText = String(cell);
      if (colIdx === row.length - 1) {
        if (cellText === 'Completada') doc.setTextColor(5, 150, 105);else if (cellText === 'En Progreso') doc.setTextColor(217, 119, 6);else if (cellText === 'Cancelada') doc.setTextColor(220, 38, 38);else doc.setTextColor(30, 41, 59);
        doc.setFont('helvetica', 'bold');
      } else if (colIdx === row.length - 2 && (cellText === 'Sí' || cellText === 'No')) {
        // Vencidos column
        if (cellText === 'Sí') doc.setTextColor(220, 38, 38);else doc.setTextColor(5, 150, 105);
        doc.setFont('helvetica', 'bold');
      } else if (cellText === 'ANULADO') {
        doc.setTextColor(148, 163, 184); // slate-400
        doc.setFont('helvetica', 'bold');
      } else if (colIdx === 0) {
        doc.setTextColor(15, 23, 42);
        doc.setFont('helvetica', 'bold');
      } else {
        doc.setTextColor(51, 65, 85);
        doc.setFont('helvetica', 'normal');
      }
      const maxW = colWidths[colIdx] - 2;
      let txt = cellText;
      while (doc.getTextWidth(txt) > maxW && txt.length > 3) txt = txt.slice(0, -1);
      if (txt !== cellText) txt = txt.slice(0, -1) + '…';
      doc.text(txt, x + colWidths[colIdx] / 2, y + rowHeight - 2, {
        align: 'center'
      });
      x += colWidths[colIdx];
    });
    y += rowHeight;
    // Sub-note row for motivoNoPago / comentarios (multi-line support)
    if (note) {
      doc.setFontSize(7);
      doc.setFont('helvetica', 'italic');
      const maxNoteW = tableW - 6;
      const noteLines: string[] = doc.splitTextToSize(note, maxNoteW);
      const lineH = 3.5;
      const notePadding = 2;
      const noteH = noteLines.length * lineH + notePadding * 2;
      // Page break if needed
      if (y + noteH > ph - 12) {
        doc.addPage();
        y = 12;
        drawHeaderRow(y);
        y += rowHeight + 1;
      }
      doc.setFillColor(255, 251, 235); // amber-50
      doc.rect(ml, y, tableW, noteH, 'F');
      doc.setDrawColor(253, 230, 138); // amber-200
      doc.setLineWidth(0.2);
      doc.rect(ml, y, tableW, noteH, 'S');
      doc.setTextColor(146, 64, 14); // amber-800
      noteLines.forEach((line: string, li: number) => {
        doc.text(line, ml + 3, y + notePadding + lineH * (li + 0.7));
      });
      y += noteH;
    }
  });
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.3);
  doc.line(ml, y, ml + tableW, y);
  return y;
}
export function ReportsPage() {
  const {
    user
  } = useAuth();
  const {
    visits,
    users,
    totalVendido: totalVendidoGlobal
  } = useApp();
  const isAdmin = user?.rolNombre === 'Administrador' || user?.permisos?.users === true;
  const [reportType, setReportType] = useState<ReportType>('diario');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedVendor, setSelectedVendor] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [productivityPage, setProductivityPage] = useState(1);
  const ITEMS_PER_PAGE = 6;
  // ── Retroactive discount map (same logic as VisitsPage) ──────────────
  const [retroDiscountMap, setRetroDiscountMap] = useState<Record<string, number>>({});
  // ── Load invoices for ventas report ──────────────────────────────────
  const [invoices, setInvoices] = useState<any[]>([]);
  const loadInvoices = useCallback(async () => {
    try {
      const {
        data,
        error
      } = await supabaseAdmin.from('invoices').select('id,numero_factura,farmacia,vendedor,vendedor_id,fecha_emision,sub_total,itbis,monto,estado').not('estado', 'eq', 'anulada').order('fecha_emision', {
        ascending: false
      }).limit(2000);
      if (data) setInvoices(data);
      if (error) console.error('Error loading invoices for reports:', error);
    } catch {}
  }, []);
  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);
  useRealtimeRefresh(['invoices'], loadInvoices);
  useEffect(() => {
    // Debounce: wait 800ms after visits change before computing
    const timer = setTimeout(() => {
      const computeRetroDiscounts = async () => {
        const cobroVisits = visits.filter(v => (v.tipoVisita === 'cobro' || v.tipoVisita === 'cobro_y_venta') && v.cobro.huboCobo && v.cobro.montoCobrado > 0 && (v.cobro.descuento || 0) === 0 && !(v.cobro as any)?.anulado);
        if (cobroVisits.length === 0) {
          setRetroDiscountMap({});
          return;
        }
        const visitFactMap: Record<string, string[]> = {};
        const allNums: string[] = [];
        cobroVisits.forEach(v => {
          const fd: any[] = (v.cobro as any).facturasDetalle || [];
          const nums = fd.length > 0 ? fd.map((f: any) => f.numeroFactura).filter(Boolean) : v.cobro.numeroFactura ? v.cobro.numeroFactura.split(',').map((n: string) => n.trim()).filter(Boolean) : [];
          if (nums.length > 0) {
            visitFactMap[v.id] = nums;
            allNums.push(...nums);
          }
        });
        const uniqueNums = [...new Set(allNums)];
        if (uniqueNums.length === 0) return;
        try {
          const {
            data: invRows
          } = await supabaseAdmin.from('invoices').select('numero_factura, pp_pct, sub_total').in('numero_factura', uniqueNums.slice(0, 200));
          if (!invRows) return;
          const ppMap: Record<string, {
            ppPct: number | null;
            subTotal: number;
          }> = {};
          invRows.forEach((row: any) => {
            ppMap[row.numero_factura] = {
              ppPct: row.pp_pct != null ? Number(row.pp_pct) : null,
              subTotal: Number(row.sub_total) || 0
            };
          });
          const newMap: Record<string, number> = {};
          Object.entries(visitFactMap).forEach(([visitId, nums]) => {
            const v = visits.find(vis => vis.id === visitId);
            if (!v) return;
            const fd: any[] = (v.cobro as any).facturasDetalle || [];
            let totalDisc = 0;
            nums.forEach(num => {
              const pp = ppMap[num];
              const savedDesc = fd.find((f: any) => f.numeroFactura === num)?.descuento || 0;
              if (savedDesc === 0 && pp && pp.ppPct != null && pp.ppPct > 0 && pp.subTotal > 0) {
                totalDisc += Math.round(pp.subTotal * (pp.ppPct / 100) * 100) / 100;
              }
            });
            if (totalDisc > 0) newMap[visitId] = totalDisc;
          });
          setRetroDiscountMap(newMap);
        } catch {
          // Silently fail
        }
      };
      computeRetroDiscounts();
    }, 800);
    return () => clearTimeout(timer);
  }, [visits]);
  // Helper: get effective cobro amount (accounting for discount)
  const getEffectiveCobro = (visit: Visit): number => {
    if ((visit.cobro as any)?.anulado) return 0;
    if (!visit.cobro.huboCobo || visit.cobro.montoCobrado <= 0) return 0;
    const savedDesc = visit.cobro.descuento || 0;
    if (savedDesc > 0) {
      const credito = visit.cobro.credito || 0;
      if (credito > 0 && Math.abs(visit.cobro.montoCobrado - credito) < 0.01) {
        return Math.round((visit.cobro.montoCobrado - savedDesc) * 100) / 100;
      }
      return visit.cobro.montoCobrado;
    }
    const retroDisc = retroDiscountMap[visit.id] || 0;
    if (retroDisc > 0) {
      return Math.round((visit.cobro.montoCobrado - retroDisc) * 100) / 100;
    }
    return visit.cobro.montoCobrado;
  };
  // Helper: get total discount for a visit (saved or retroactive)
  const getVisitDiscount = (visit: Visit): number => {
    const savedDesc = visit.cobro.descuento || 0;
    if (savedDesc > 0) return savedDesc;
    return retroDiscountMap[visit.id] || 0;
  };
  const vendors = users.filter(u => u.rolNombre !== 'Administrador' && u.activo && u.permisos?.['new-visit'] === true);
  const vendorOptions = [{
    value: '',
    label: 'Todos los vendedores'
  }, ...vendors.map(v => ({
    value: v.id,
    label: v.nombre
  }))];
  const reportTypeOptions = [{
    value: 'diario',
    label: 'Reporte Diario',
    icon: CalendarIcon
  }, {
    value: 'semanal',
    label: 'Reporte Semanal',
    icon: CalendarIcon
  }, {
    value: 'cobros',
    label: 'Reporte de Cobros',
    icon: DollarSignIcon
  }, {
    value: 'ventas',
    label: 'Reporte de Ventas',
    icon: ShoppingCartIcon
  }, {
    value: 'visitas',
    label: 'Reporte de Visitas',
    icon: MapPinIcon
  }, {
    value: 'productividad',
    label: 'Productividad',
    icon: TrendingUpIcon
  }];
  const filteredVisits = visits.filter(v => isAdmin || v.vendedorId === user?.id).filter(v => {
    if (selectedVendor && v.vendedorId !== selectedVendor) return false;
    // ── Filter by report type ──────────────────────────────────────────────
    if (reportType === 'cobros') {
      // Only visits where a collection happened
      if (!v.cobro.huboCobo) return false;
    }
    if (reportType === 'ventas') {
      // Only visits where a sale happened
      if (!v.venta.huboVenta) return false;
    }
    if (reportType === 'diario') {
      // Only today's visits (unless user set a custom date range)
      if (!dateFrom && !dateTo) {
        const today = new Date();
        const visitDate = new Date(v.fecha);
        if (visitDate.getFullYear() !== today.getFullYear() || visitDate.getMonth() !== today.getMonth() || visitDate.getDate() !== today.getDate()) return false;
      }
    }
    if (reportType === 'semanal') {
      // Only last 7 days (unless user set a custom date range)
      if (!dateFrom && !dateTo) {
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        weekAgo.setHours(0, 0, 0, 0);
        const visitDate = new Date(v.fecha);
        if (visitDate < weekAgo) return false;
      }
    }
    // ── Date range filter (local timezone) ────────────────────────────────
    if (dateFrom || dateTo) {
      const visitDateStr = new Date(v.fecha).toLocaleDateString('en-CA', {
        timeZone: 'America/Santo_Domingo'
      });
      if (dateFrom && visitDateStr < dateFrom) return false;
      if (dateTo && visitDateStr > dateTo) return false;
    }
    return true;
  });
  // ── Unified Sales Logic for "ventas" report ──────────────────────────────
  const unifiedSales: SaleItem[] = reportType === 'ventas' ? (() => {
    // Use ONLY invoices as the authoritative source for sales.
    // Visit-sales are unreliable because pedidos can be deleted after the visit
    // was recorded, leaving stale huboVenta=true entries.
    const invoiceSales: SaleItem[] = invoices.filter(inv => isAdmin || inv.vendedor_id === user?.id).filter(inv => {
      if (selectedVendor && inv.vendedor_id !== selectedVendor) return false;
      const saleDate = new Date(inv.fecha_emision).toLocaleDateString('en-CA', {
        timeZone: 'America/Santo_Domingo'
      });
      if (dateFrom && saleDate < dateFrom) return false;
      if (dateTo && saleDate > dateTo) return false;
      return true;
    }).map(inv => ({
      id: `f-${inv.id}`,
      clienteNombre: inv.farmacia,
      vendedorId: inv.vendedor_id || '',
      vendedorNombre: inv.vendedor || '',
      fecha: new Date(inv.fecha_emision),
      zona: '',
      montoTotal: inv.monto || 0,
      subTotal: inv.sub_total || 0,
      itbis: inv.itbis || 0,
      referencia: inv.numero_factura || '',
      comentarios: '',
      source: 'factura' as const
    }));
    return invoiceSales.sort((a, b) => b.fecha.getTime() - a.fecha.getTime());
  })() : [];
  const totalVisitas = reportType === 'ventas' ? unifiedSales.length : filteredVisits.length;
  const visitasCompletadas = filteredVisits.filter(v => v.estado === 'completada').length;
  const totalCobrado = filteredVisits.reduce((sum, v) => {
    if ((v.cobro as any)?.anulado) return sum;
    return sum + getEffectiveCobro(v);
  }, 0);
  const totalVendido = reportType === 'ventas' ? unifiedSales.reduce((sum, s) => sum + s.montoTotal, 0) : filteredVisits.reduce((sum, v) => sum + (v.venta.huboVenta ? v.venta.montoVenta : 0), 0);
  const tiempoPromedio = filteredVisits.filter(v => v.tiempoTotal).reduce((sum, v) => sum + (v.tiempoTotal || 0), 0) / (filteredVisits.filter(v => v.tiempoTotal).length || 1);
  const productivityByVendor = isAdmin ? vendors.map(vendor => {
    const vendorVisits = filteredVisits.filter(v => v.vendedorId === vendor.id);
    return {
      vendedor: vendor.nombre,
      visitas: vendorVisits.length,
      completadas: vendorVisits.filter(v => v.estado === 'completada').length,
      cobrado: vendorVisits.reduce((sum, v) => {
        if ((v.cobro as any)?.anulado) return sum;
        return sum + getEffectiveCobro(v);
      }, 0),
      vendido: vendorVisits.reduce((sum, v) => sum + v.venta.montoVenta, 0),
      tiempoPromedio: Math.round(vendorVisits.filter(v => v.tiempoTotal).reduce((sum, v) => sum + (v.tiempoTotal || 0), 0) / (vendorVisits.filter(v => v.tiempoTotal).length || 1))
    };
  }) : [];
  // Sorting setup for productivity table
  const {
    items: sortedProductivity,
    requestSort,
    sortConfig
  } = useSortableData(productivityByVendor, {
    vendedor: p => p.vendedor,
    visitas: p => p.visitas,
    completadas: p => p.completadas,
    cobrado: p => p.cobrado,
    vendido: p => p.vendido,
    tiempoPromedio: p => p.tiempoPromedio,
    eficiencia: p => p.visitas > 0 ? Math.round(p.completadas / p.visitas * 100) : 0
  });
  // Default sort by vendedor if no sort config
  const finalProductivity = sortConfig ? sortedProductivity : [...sortedProductivity].sort((a, b) => a.vendedor.localeCompare(b.vendedor));
  const formatCurrency = (amount: number) => new Intl.NumberFormat('es-DO', {
    style: 'currency',
    currency: 'DOP',
    minimumFractionDigits: 0
  }).format(amount);
  const formatDate = (d: Date) => new Date(d).toLocaleDateString('es-DO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
  // Readable long format for PDF date range
  const formatDateLong = (d: string) => new Date(d + 'T00:00:00').toLocaleDateString('es-DO', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  // Reset pages when filters/type change
  useEffect(() => {
    setCurrentPage(1);
    setProductivityPage(1);
  }, [reportType, dateFrom, dateTo, selectedVendor]);
  // Pagination derived values
  const totalVisitPages = Math.ceil(filteredVisits.length / ITEMS_PER_PAGE);
  const paginatedVisits = filteredVisits.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const totalProdPages = Math.ceil(finalProductivity.length / ITEMS_PER_PAGE);
  const paginatedProductivity = finalProductivity.slice((productivityPage - 1) * ITEMS_PER_PAGE, productivityPage * ITEMS_PER_PAGE);
  const totalSalesPages = Math.ceil(unifiedSales.length / ITEMS_PER_PAGE);
  const paginatedSales = unifiedSales.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const reportLabel = reportTypeOptions.find(r => r.value === reportType)?.label || 'Reporte';
  // ─── PDF BUILDER ─────────────────────────────────────────────────────────────
  const buildPDF = async (): Promise<jsPDF> => {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });
    const pw = doc.internal.pageSize.getWidth();
    const ph = doc.internal.pageSize.getHeight();
    const ml = 12;
    const mr = 12;
    const cw = pw - ml - mr;
    const label = reportTypeOptions.find(r => r.value === reportType)?.label || 'Reporte';
    const isProductividad = reportType === 'productividad' && isAdmin;
    const genDate = new Date().toLocaleDateString('es-DO', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
    const periodText = dateFrom || dateTo ? (dateFrom ? formatDateLong(dateFrom) : 'Inicio') + '  al  ' + (dateTo ? formatDateLong(dateTo) : 'Hoy') : 'Todos los registros';
    const vendorName = selectedVendor ? vendors.find(v => v.id === selectedVendor)?.nombre || '' : '';
    // ── HEADER BAND ──────────────────────────────────────────────────────────
    const headerH = 36;
    doc.setFillColor(13, 27, 70);
    doc.rect(0, 0, pw, headerH, 'F');
    // Logo white rounded box
    const logoBase64 = await getLogoBase64();
    if (logoBase64) {
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(ml, 5, 26, 26, 3, 3, 'F');
      doc.addImage(logoBase64, 'PNG', ml + 1, 6, 24, 24);
    }
    // Company name
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text('Miladys Rodriguez & Asoc.', ml + 30, 16);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(123, 163, 216);
    doc.text('Sistema de Control de Vendedores', ml + 30, 23);
    // Date top right
    doc.setFontSize(8);
    doc.setTextColor(123, 163, 216);
    doc.text('Generado: ' + genDate, pw - mr, 16, {
      align: 'right'
    });
    // ── ACCENT BAR ───────────────────────────────────────────────────────────
    doc.setFillColor(41, 82, 196);
    doc.rect(0, headerH, pw, 2, 'F');
    // ── TITLE SECTION ────────────────────────────────────────────────────────
    let y = headerH + 2;
    // Light background strip
    const hasDateRange = dateFrom || dateTo;
    const titleStripH = hasDateRange ? 30 : 24;
    doc.setFillColor(248, 250, 255);
    doc.rect(0, y, pw, titleStripH, 'F');
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.3);
    doc.line(0, y + titleStripH, pw, y + titleStripH);
    y += 6;
    doc.setTextColor(13, 27, 70);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(label.toUpperCase(), ml, y + 5);
    // Records pill (top right)
    const pillW = 30;
    doc.setFillColor(41, 82, 196);
    doc.roundedRect(pw - mr - pillW, y, pillW, 8, 2, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.text((reportType === 'ventas' ? unifiedSales.length : filteredVisits.length) + ' registros', pw - mr - pillW / 2, y + 5.5, {
      align: 'center'
    });
    // Date range box - prominent display
    if (hasDateRange) {
      const boxY = y + 10;
      const boxH = 10;
      const boxW = pw - ml - mr;
      // Blue-tinted background box
      doc.setFillColor(234, 240, 255); // light blue bg
      doc.setDrawColor(41, 82, 196);
      doc.setLineWidth(0.4);
      doc.roundedRect(ml, boxY, boxW, boxH, 2, 2, 'FD');
      // Calendar icon indicator (small blue square)
      doc.setFillColor(41, 82, 196);
      doc.roundedRect(ml + 2, boxY + 2, 6, 6, 1, 1, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.text('F', ml + 5, boxY + 6.2, {
        align: 'center'
      });
      // "Período:" label
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(41, 82, 196);
      const periodLabel = 'Período:  ';
      doc.text(periodLabel, ml + 10, boxY + 6.5);
      const periodLabelW = doc.getTextWidth(periodLabel);
      // Date range text
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(13, 27, 70);
      doc.text(periodText, ml + 10 + periodLabelW, boxY + 6.5);
      // Vendor name if selected (right-aligned in the box)
      if (vendorName) {
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(41, 82, 196);
        const vendorStr = 'Vendedor: ';
        doc.text(vendorStr, pw - mr - 4 - doc.getTextWidth(vendorName) - doc.getTextWidth(vendorStr), boxY + 6.5);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(13, 27, 70);
        doc.text(vendorName, pw - mr - 4, boxY + 6.5, {
          align: 'right'
        });
      }
    } else {
      // No date range - just show "Todos los registros" simply
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 116, 139);
      doc.text('Período: ' + periodText, ml, y + 12);
      if (vendorName) {
        const baseText = 'Período: ' + periodText;
        const baseW = doc.getTextWidth(baseText);
        doc.text('   |   Vendedor: ' + vendorName, ml + baseW, y + 12);
      }
    }
    y = headerH + 2 + titleStripH + 2;
    // ── STATS ROW ────────────────────────────────────────────────────────────
    const statsH = 18;
    const statW = cw / 5;
    doc.setFillColor(248, 250, 255);
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.3);
    doc.rect(ml, y, cw, statsH, 'FD');
    const stats = [{
      label: 'VISITAS',
      value: String(totalVisitas),
      r: 13,
      g: 27,
      b: 70
    }, {
      label: 'COMPLETADAS',
      value: String(visitasCompletadas),
      r: 13,
      g: 27,
      b: 70
    }, {
      label: 'COBRADO',
      value: formatCurrency(totalCobrado),
      r: 5,
      g: 150,
      b: 105
    }, {
      label: 'VENDIDO',
      value: formatCurrency(totalVendido),
      r: 41,
      g: 82,
      b: 196
    }, {
      label: 'T. PROMEDIO',
      value: Math.round(tiempoPromedio) + ' min',
      r: 13,
      g: 27,
      b: 70
    }];
    stats.forEach((stat, i) => {
      const sx = ml + i * statW;
      if (i > 0) {
        doc.setDrawColor(226, 232, 240);
        doc.setLineWidth(0.2);
        doc.line(sx, y + 2, sx, y + statsH - 2);
      }
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(stat.r, stat.g, stat.b);
      doc.text(stat.value, sx + statW / 2, y + 8, {
        align: 'center'
      });
      doc.setFontSize(6.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(148, 163, 184);
      doc.text(stat.label, sx + statW / 2, y + 14, {
        align: 'center'
      });
    });
    y += statsH + 7;
    // ── SECTION TITLE ────────────────────────────────────────────────────────
    const sectionTitle = isProductividad ? 'PRODUCTIVIDAD POR VENDEDOR' : 'DETALLE DE REGISTROS';
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(13, 27, 70);
    doc.text(sectionTitle, ml, y);
    doc.setDrawColor(41, 82, 196);
    doc.setLineWidth(0.6);
    doc.line(ml, y + 1.5, ml + doc.getTextWidth(sectionTitle), y + 1.5);
    y += 5;
    // ── TABLE ────────────────────────────────────────────────────────────────
    const tipoMap: Record<string, string> = {
      cobro: 'Cobro',
      venta: 'Venta',
      cobro_y_venta: 'C+V',
      seguimiento: 'Seguim.'
    };
    if (isProductividad) {
      const headers = ['Vendedor', 'Visitas', 'Completadas', 'Cobrado', 'Vendido', 'T. Prom.', 'Eficiencia'];
      const colWidths = [42, 18, 22, 28, 28, 18, 22];
      const rows = finalProductivity.map(row => {
        const eff = row.visitas > 0 ? Math.round(row.completadas / row.visitas * 100) : 0;
        return [row.vendedor, String(row.visitas), String(row.completadas), formatCurrency(row.cobrado), formatCurrency(row.vendido), row.tiempoPromedio + ' min', eff + '%'];
      });
      drawTable(doc, headers, rows, y, ml, mr, colWidths);
    } else if (reportType === 'ventas') {
      const headers = ['Cliente', 'Vendedor', 'Fecha', 'Referencia', 'SubTotal', 'ITBIS', 'Total', 'Origen'];
      const colWidths = [40, 30, 20, 20, 20, 15, 25, 16];
      const rows = unifiedSales.map(s => [s.clienteNombre, s.vendedorNombre, formatDate(s.fecha), s.referencia || '—', formatCurrency(s.subTotal), formatCurrency(s.itbis), formatCurrency(s.montoTotal), s.source === 'factura' ? 'Factura' : 'Visita']);
      const subNotes = unifiedSales.map(s => s.comentarios ? `Comentario: ${s.comentarios}` : '');
      drawTable(doc, headers, rows, y, ml, mr, colWidths, 7, subNotes);
    } else {
      // Always show Vendedor column (all clients are farmacias)
      const headers = ['Farmacia', 'Vendedor', 'Fecha', 'Zona', 'Tipo', 'Cobro', 'Venta', 'Vencidos', 'Estado'];
      const colWidths = [28, 22, 16, 18, 12, 22, 20, 16, 22];
      const rows = filteredVisits.map(v => {
        const estadoLabel = v.estado === 'completada' ? 'Completada' : v.estado === 'en_progreso' ? 'En Progreso' : 'Cancelada';
        const vencidosLabel = v.productosVencidos === true ? 'Sí' : v.productosVencidos === false ? 'No' : '—';
        return [v.clienteNombre, v.vendedorNombre, formatDate(v.fecha), v.zona, tipoMap[v.tipoVisita] || v.tipoVisita, (v.cobro as any)?.anulado ? 'ANULADO' : v.cobro.huboCobo ? formatCurrency(getEffectiveCobro(v)) : '—', v.venta.huboVenta ? formatCurrency(v.venta.montoVenta) : '—', vencidosLabel, estadoLabel];
      });
      // Sub-notes: motivoNoPago + comentarios per row
      const subNotes = filteredVisits.map(v => {
        const parts: string[] = [];
        if (v.motivoNoPago) parts.push(`Motivo: ${v.motivoNoPago}`);
        if (v.comentarios) parts.push(`Comentario: ${v.comentarios}`);
        return parts.join(' | ');
      });
      drawTable(doc, headers, rows, y, ml, mr, colWidths, 7, subNotes);
    }
    // ── FOOTER (all pages) ───────────────────────────────────────────────────
    const totalPages = (doc as any).internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFillColor(13, 27, 70);
      doc.rect(0, ph - 9, pw, 9, 'F');
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(74, 111, 165);
      doc.text('Miladys Rodriguez & Asoc. © 2025 — Sistema de Control de Vendedores', ml, ph - 3);
      doc.text('Pagina ' + i + ' de ' + totalPages, pw - mr, ph - 3, {
        align: 'right'
      });
    }
    return doc;
  };
  // ─── DOWNLOAD as .pdf ────────────────────────────────────────────────────────
  const handleDownloadPDF = async () => {
    setIsDownloading(true);
    try {
      const doc = await buildPDF();
      const dateStr = new Date().toISOString().slice(0, 10);
      doc.save('reporte-' + reportType + '-' + dateStr + '.pdf');
    } catch (err) {
      console.error('Error al descargar PDF:', err);
      alert('Error al generar el PDF. Intenta de nuevo.');
    } finally {
      setIsDownloading(false);
    }
  };
  // ─── VIEW / PRINT in new tab ─────────────────────────────────────────────────
  const handlePrint = async () => {
    setIsExporting(true);
    try {
      const doc = await buildPDF();
      const blobUrl = doc.output('bloburl');
      window.open(String(blobUrl), '_blank');
    } catch (err) {
      console.error('Error al exportar PDF:', err);
      alert('Error al generar el PDF. Intenta de nuevo.');
    } finally {
      setIsExporting(false);
    }
  };
  return <div className="space-y-6" data-id="element-3933">
      <PageHeader title="Reportes" subtitle="Análisis y métricas de rendimiento" icon={<BarChart3Icon className="w-6 h-6" data-id="element-3935" />} data-id="element-3934">
        <div className="flex items-center gap-2" data-id="element-3936">
          <Button variant="secondary" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-3938" />} onClick={handleDownloadPDF} isLoading={isDownloading} data-id="element-3937">
            {isDownloading ? 'Generando...' : 'Descargar PDF'}
          </Button>
          <Button leftIcon={isExporting ? undefined : <PrinterIcon className="w-4 h-4" data-id="element-3940" />} onClick={handlePrint} isLoading={isExporting} data-id="element-3939">
            {isExporting ? 'Abriendo...' : 'Ver / Imprimir'}
          </Button>
        </div>
      </PageHeader>

      {/* Report type selection */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 animate-stagger-1" data-id="element-3941">
        {reportTypeOptions.map(option => {
        const Icon = option.icon;
        const isActive = reportType === option.value;
        return <button key={option.value} onClick={() => setReportType(option.value as ReportType)} className="p-4 rounded-xl border-2 transition-all text-left" style={isActive ? {
          borderColor: '#2952C4',
          backgroundColor: '#eef2fc'
        } : {
          borderColor: '#e2e8f0',
          backgroundColor: 'white'
        }} data-id="element-3942">
              <div className="mb-2" style={{
            color: isActive ? '#2952C4' : '#94a3b8'
          }} data-id="element-3943">
                <Icon className="w-5 h-5" data-id="element-3944" />
              </div>
              <p className="text-sm font-medium font-heading" style={{
            color: isActive ? '#0d1b46' : '#334155'
          }} data-id="element-3945">
                {option.label}
              </p>
            </button>;
      })}
      </div>

      {/* Filters */}
      <Card className="animate-stagger-2" data-id="element-3946">
        <CardHeader title="Filtros del Reporte" data-id="element-3947" />
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4" data-id="element-3948">
          <div className="col-span-1 min-w-0" data-id="element-3949">
            <Input label="Fecha Desde" type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} data-id="element-3950" />
          </div>
          <div className="col-span-1 min-w-0" data-id="element-3951">
            <Input label="Fecha Hasta" type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} data-id="element-3952" />
          </div>
          {isAdmin && <div className="col-span-1 min-w-0" data-id="element-3953">
              <Select label="Vendedor" options={vendorOptions} value={selectedVendor} onChange={e => setSelectedVendor(e.target.value)} data-id="element-3954" />
            </div>}
          <div className={`flex items-end ${isAdmin ? 'col-span-1' : 'col-span-2'} md:col-span-1`} data-id="element-3955">
            <Button variant="secondary" className="w-full" onClick={() => {
            setDateFrom('');
            setDateTo('');
            setSelectedVendor('');
          }} data-id="element-3956">
              Limpiar
            </Button>
          </div>
        </div>
      </Card>

      {/* Report summary */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 sm:gap-4 animate-stagger-2" data-id="element-3957">
        <StatsCard title={reportType === 'cobros' ? 'Visitas con Cobro' : reportType === 'ventas' ? 'Visitas con Venta' : 'Total Visitas'} value={totalVisitas} icon={<MapPinIcon className="w-5 h-5" data-id="element-3959" />} variant="primary" data-id="element-3958" />
        <StatsCard title="Completadas" value={visitasCompletadas} icon={<CheckCircleIcon className="w-5 h-5" data-id="element-3961" />} variant="success" data-id="element-3960" />
        <StatsCard title="Total Cobrado" value={formatCurrency(totalCobrado)} icon={<DollarSignIcon className="w-5 h-5" data-id="element-3963" />} variant="success" data-id="element-3962" />
        <StatsCard title="Total Vendido" value={formatCurrency(totalVendido)} icon={<ShoppingCartIcon className="w-5 h-5" data-id="element-3965" />} variant="warning" data-id="element-3964" />
        <StatsCard title="Tiempo Promedio" value={`${Math.round(tiempoPromedio)} min`} icon={<TrendingUpIcon className="w-5 h-5" data-id="element-3967" />} variant="default" data-id="element-3966" />
      </div>

      {/* Productivity table */}
      {isAdmin && reportType === 'productividad' && <Card padding="none" className="animate-stagger-3" data-id="element-3968">
          <div className="px-6 py-5 border-b border-slate-100" data-id="element-3969">
            <CardHeader title="Productividad por Vendedor" subtitle="Resumen de actividad del equipo comercial" data-id="element-3970" />
          </div>
          <div className="overflow-x-auto" data-id="element-3971">
            <table className="w-full" data-id="element-3972">
              <thead data-id="element-3973">
                <tr className="bg-slate-50/80 border-b border-slate-100" data-id="element-3974">
                  <SortableTableHeader label="Vendedor" sortKey="vendedor" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-3975" />
                  <SortableTableHeader label="Visitas" sortKey="visitas" align="center" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-3976" />
                  <SortableTableHeader label="Completadas" sortKey="completadas" align="center" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-3977" />
                  <SortableTableHeader label="Cobrado" sortKey="cobrado" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-3978" />
                  <SortableTableHeader label="Vendido" sortKey="vendido" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-3979" />
                  <SortableTableHeader label="Tiempo Prom." sortKey="tiempoPromedio" align="center" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-3980" />
                  <SortableTableHeader label="Eficiencia" sortKey="eficiencia" align="center" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-3981" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-3982">
                {paginatedProductivity.map(row => {
              const efficiency = row.visitas > 0 ? Math.round(row.completadas / row.visitas * 100) : 0;
              return <tr key={row.vendedor} className="hover:bg-slate-50/50 transition-colors" data-id="element-3983">
                      <td className="px-5 py-4 font-semibold text-slate-900" data-id="element-3984">
                        {row.vendedor}
                      </td>
                      <td className="px-5 py-4 text-center text-slate-700" data-id="element-3985">
                        {row.visitas}
                      </td>
                      <td className="px-5 py-4 text-center text-slate-700" data-id="element-3986">
                        {row.completadas}
                      </td>
                      <td className="px-5 py-4 text-right text-emerald-600 font-semibold" data-id="element-3987">
                        {formatCurrency(row.cobrado)}
                      </td>
                      <td className="px-5 py-4 text-right font-semibold" style={{
                  color: '#2952C4'
                }} data-id="element-3988">
                        {formatCurrency(row.vendido)}
                      </td>
                      <td className="px-5 py-4 text-center text-slate-700" data-id="element-3989">
                        {row.tiempoPromedio} min
                      </td>
                      <td className="px-5 py-4 text-center" data-id="element-3990">
                        <Badge variant={efficiency >= 80 ? 'success' : efficiency >= 50 ? 'warning' : 'danger'} data-id="element-3991">
                          {efficiency}%
                        </Badge>
                      </td>
                    </tr>;
            })}
              </tbody>
            </table>
          </div>
          <Pagination currentPage={productivityPage} totalPages={totalProdPages} onPageChange={setProductivityPage} totalItems={productivityByVendor.length} itemsPerPage={ITEMS_PER_PAGE} data-id="element-3992" />
        </Card>}

      {/* Visits detail */}
      {reportType !== 'productividad' && <Card padding="none" className="animate-stagger-3" data-id="element-3993">
          <div className="px-6 py-5 border-b border-slate-100" data-id="element-3994">
            <CardHeader title={reportType === 'cobros' ? 'Detalle de Cobros' : reportType === 'ventas' ? 'Detalle de Ventas' : reportType === 'diario' ? 'Visitas de Hoy' : reportType === 'semanal' ? 'Visitas de los Últimos 7 Días' : 'Detalle de Registros'} subtitle={`${reportType === 'ventas' ? unifiedSales.length : filteredVisits.length} registros en el período seleccionado`} data-id="element-3995" />
          </div>
          <div className="divide-y divide-slate-100" data-id="element-3996">
            {(reportType === 'ventas' ? unifiedSales.length : filteredVisits.length) === 0 ? <div className="p-16 text-center animate-fade-in" data-id="element-3997">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-2xl mb-4" data-id="element-3998">
                  <FileTextIcon className="w-8 h-8 text-slate-400" data-id="element-3999" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-1" data-id="element-4000">
                  No hay registros
                </h3>
                <p className="text-slate-500 text-sm" data-id="element-4001">
                  {reportType === 'cobros' ? 'No hay cobros en el período seleccionado' : reportType === 'ventas' ? 'No hay ventas en el período seleccionado' : 'No hay registros para el período seleccionado'}
                </p>
              </div> : reportType === 'ventas' ? paginatedSales.map(sale => <div key={sale.id} className="px-6 py-4 hover:bg-slate-50/50 transition-colors" data-id="element-4002">
                  <div className="flex items-start justify-between" data-id="element-4003">
                    <div className="flex-1 min-w-0" data-id="element-4004">
                      <div className="flex items-center gap-3" data-id="element-4005">
                        <p className="font-semibold text-slate-900" data-id="element-4006">
                          {sale.clienteNombre}
                        </p>
                        {sale.referencia && <Badge variant={sale.source === 'factura' ? 'warning' : 'info'} data-id="element-4007">
                            {sale.referencia}
                          </Badge>}
                        <Badge variant={sale.source === 'factura' ? 'default' : 'success'} data-id="element-4008">
                          {sale.source === 'factura' ? 'Factura' : 'Visita'}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-500 mt-0.5" data-id="element-4009">
                        {sale.vendedorNombre} • {formatDate(sale.fecha)}
                        {sale.zona && ` • ${sale.zona}`}
                      </p>
                      {sale.comentarios && <p className="text-xs mt-1.5 px-2 py-1 bg-blue-50 border border-blue-200 rounded-md text-blue-800 inline-flex items-center gap-1" data-id="element-4010">
                          <span className="font-semibold" data-id="element-4011">Comentario:</span>{' '}
                          {sale.comentarios}
                        </p>}
                    </div>
                    <div className="text-right ml-4 flex-shrink-0" data-id="element-4012">
                      <p className="text-lg font-bold text-[#2952C4]" data-id="element-4013">
                        {formatCurrency(sale.montoTotal)}
                      </p>
                      <div className="mt-1 text-xs text-slate-500 space-y-0.5" data-id="element-4014">
                        <p data-id="element-4015">
                          Sub:{' '}
                          <span className="font-semibold text-slate-700" data-id="element-4016">
                            {formatCurrency(sale.subTotal)}
                          </span>
                        </p>
                        <p data-id="element-4017">
                          ITBIS:{' '}
                          <span className="font-semibold text-slate-600" data-id="element-4018">
                            {formatCurrency(sale.itbis)}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>) : paginatedVisits.map(visit => <div key={visit.id} className="px-6 py-4 hover:bg-slate-50/50 transition-colors" data-id="element-4019">
                  <div className="flex items-start justify-between" data-id="element-4020">
                    <div className="flex-1 min-w-0" data-id="element-4021">
                      <p className="font-semibold text-slate-900" data-id="element-4022">
                        {visit.clienteNombre}
                      </p>
                      <p className="text-sm text-slate-500 mt-0.5" data-id="element-4023">
                        {visit.vendedorNombre} •{' '}
                        {new Date(visit.fecha).toLocaleDateString('es-DO')} •{' '}
                        {visit.zona}
                      </p>
                      {visit.motivoNoPago && <p className="text-xs mt-1.5 px-2 py-1 bg-amber-50 border border-amber-200 rounded-md text-amber-800 inline-flex items-center gap-1" data-id="element-4024">
                          <span className="font-semibold" data-id="element-4025">Motivo no pago:</span>{' '}
                          {visit.motivoNoPago}
                        </p>}
                      {visit.comentarios && <p className="text-xs mt-1.5 px-2 py-1 bg-blue-50 border border-blue-200 rounded-md text-blue-800 inline-flex items-center gap-1 ml-1" data-id="element-4026">
                          <span className="font-semibold" data-id="element-4027">Comentario:</span>{' '}
                          {visit.comentarios}
                        </p>}
                      {visit.productosVencidos !== null && visit.productosVencidos !== undefined && <p className={`text-xs mt-1.5 px-2 py-1 rounded-md inline-flex items-center gap-1 ml-1 ${visit.productosVencidos ? 'bg-rose-50 border border-rose-200 text-rose-700' : 'bg-emerald-50 border border-emerald-200 text-emerald-700'}`} data-id="element-4028">
                            <span className="font-semibold" data-id="element-4029">
                              Productos vencidos:
                            </span>{' '}
                            {visit.productosVencidos ? 'Sí' : 'No'}
                          </p>}
                    </div>
                    <div className="text-right ml-4 flex-shrink-0" data-id="element-4030">
                      {visit.cobro.huboCobo && ((visit.cobro as any)?.anulado ? <p className="text-slate-400 font-semibold text-sm line-through" data-id="element-4031">
                            {formatCurrency(visit.cobro.montoCobrado)}
                            <span className="no-underline ml-1.5 text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded font-bold align-middle" data-id="element-4032">
                              ANULADO
                            </span>
                          </p> : <div data-id="element-4033">
                            <p className="text-emerald-600 font-semibold text-sm" data-id="element-4034">
                              {formatCurrency(getEffectiveCobro(visit))}
                            </p>
                            {getVisitDiscount(visit) > 0 && <p className="text-[10px] text-emerald-500 font-medium" data-id="element-4035">
                                PP: -{formatCurrency(getVisitDiscount(visit))}
                              </p>}
                          </div>)}
                      {visit.venta.huboVenta && <p className="font-semibold text-sm" style={{
                color: '#2952C4'
              }} data-id="element-4036">
                          {formatCurrency(visit.venta.montoVenta)}
                        </p>}
                      <Badge variant={visit.estado === 'completada' ? 'success' : visit.estado === 'en_progreso' ? 'warning' : 'danger'} className="mt-1" data-id="element-4037">
                        {visit.estado === 'completada' ? 'Completada' : visit.estado === 'en_progreso' ? 'En Progreso' : 'Cancelada'}
                      </Badge>
                    </div>
                  </div>
                </div>)}
          </div>
          <Pagination currentPage={currentPage} totalPages={reportType === 'ventas' ? totalSalesPages : totalVisitPages} onPageChange={setCurrentPage} totalItems={reportType === 'ventas' ? unifiedSales.length : filteredVisits.length} itemsPerPage={ITEMS_PER_PAGE} data-id="element-4038" />
        </Card>}
    </div>;
}